import {useMemo, createElement} from "react";
import {useFreelo} from "hooks/freelo/useFreelo";
import {SearchProvider, SearchableItem} from "@/hooks/search/usePageSearch";
import {Board, Card} from "@freetech/models/freelo";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import {NestedBoard} from "types/freelo/nested";

export const useFreeloBoardsSearchProvider = (): SearchProvider => {
  const {boards, isLoading, getAccessibleBoards} = useFreelo();

  const searchableItems = useMemo((): SearchableItem[] => {
    if (!boards || isLoading) {
      return [];
    }

    const accessibleBoards = getAccessibleBoards(boards);
    const items: SearchableItem[] = [];

    accessibleBoards.forEach((board: NestedBoard) => {
      items.push({
        id: `board-${board.id}`,
        title: board.name,
        segment: "freelo",
        path: `/freelo/${board.id}`,
        icon: createElement(ViewKanbanIcon),
        description: `Freelo Board - ${board.lists?.length || 0} lists, ${board.cards?.length || 0} cards`,
        category: "Freelo Boards",
        type: "board",
      });

      board.cards.forEach((card: Card) => {
        items.push({
          id: `card-${card.id}`,
          title: card.title,
          segment: "freelo",
          path: `/freelo/${board.id}?card=${card.id}`,
          icon: createElement(ViewKanbanIcon),
          description: `Card in ${board.name} > ${board.lists.find(list => list.id === card.listId)?.name || "Unknown List"}`,
          category: "Freelo Cards",
          type: "card",
        });
      });
    });

    return items;
  }, [boards, isLoading, getAccessibleBoards]);

  return {
    getItems: () => searchableItems,
    isLoading,
  };
};
